import axios from "axios";
import { supabase } from "./supabaseClient";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function authHeader() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function agentChat(sessionId, message, onProgress) {
  const headers = await authHeader();
  // Start the task as a background job, then poll the MongoDB-backed job record until
  // it completes. This lets a long tool-using agent task run to completion server-side
  // even when it takes longer than a single request can stay open — the user never
  // types "continue". `onProgress({status, progress, activity})` is called on each poll
  // so the UI can render a real 0–100% bar.
  const { data: started } = await axios.post(
    `${API}/admin/agent/chat`,
    { session_id: sessionId, message },
    { headers, timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) return started; // fallback (older backend returning reply directly)
  if (onProgress) onProgress({ status: started.status || "queued", progress: 0, activity: "Queued" });

  const startedAt = Date.now();
  const MAX_MS = 30 * 60 * 1000; // 30 min hard stop on polling
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 1500));
    if (Date.now() - startedAt > MAX_MS) {
      return { reply: "⚠️ The task is taking unusually long. It may still be running — check back shortly.", steps: [] };
    }
    try {
      const freshHeaders = await authHeader();
      const { data: job } = await axios.get(`${API}/admin/agent/job/${jobId}`, {
        headers: freshHeaders,
        timeout: 30000,
      });
      pollErrors = 0;
      if (onProgress) {
        onProgress({
          status: job.status,
          progress: typeof job.progress === "number" ? job.progress : 0,
          activity: job.activity || "Working",
        });
      }
      if (job.status === "completed" || job.status === "failed") {
        return { reply: job.reply, steps: job.steps || [], usage: job.usage };
      }
    } catch (e) {
      // A transient 404 right after POST (record not yet visible) or a network blip:
      // retry a few times before giving up so we never surface a false "expired job".
      if (++pollErrors >= 6) throw e;
    }
  }
}

export async function agentUsage() {
  const headers = await authHeader();
  const { data } = await axios.get(`${API}/admin/agent/usage`, { headers });
  return data;
}
